import { useSeekerCCState } from '@/components/AppState';
import Header from '@/components/Header';
import useResponsive from '@/components/hooks/useResponsive';
import StickyCTAButton from '@/components/shared/StickyCTAButton/StickyCTAButton';
import { SxClassProps } from '@/types/common';
import { Pill, StatelessSelector } from '@care/react-component-lib';
import { Button, Grid, Typography } from '@mui/material';
import { FormikProvider } from 'formik';
import Head from 'next/head';
import {
  CareKind,
  DayCareKind,
  DefaultCareKind,
  SeekerOverhaulCCM1Labels,
  SeekerOverhaulCCM1V2Descriptions,
} from '@/types/seekerCC';

const classes: SxClassProps = {
  selector: (theme) => ({
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(2),
    },
    '& .MuiListItem-root': {
      marginBottom: theme.spacing(0),
      padding: theme.spacing(theme.spacing(1), 0),

      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(-0.5),
      },

      '& > label': {
        height: 'auto',
        padding: theme.spacing(2, 1, 2, 2.5),
      },
    },
  }),
  mainContainer: (theme) => ({
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  }),
};

const inHomeOptions = [
  {
    label: SeekerOverhaulCCM1Labels.NANNIES_RECURRING_BABYSITTERS,
    value: DefaultCareKind.NANNIES_RECURRING_BABYSITTERS,
    description: SeekerOverhaulCCM1V2Descriptions.NANNIES_RECURRING_BABYSITTERS,
  },
  {
    label: SeekerOverhaulCCM1Labels.ONE_TIME_BABYSITTERS,
    value: DefaultCareKind.ONE_TIME_BABYSITTERS,
    description: SeekerOverhaulCCM1V2Descriptions.ONE_TIME_BABYSITTERS,
  },
  {
    label: SeekerOverhaulCCM1Labels.CHILDCARE,
    value: DefaultCareKind.CHILDCARE,
    description: SeekerOverhaulCCM1V2Descriptions.CHILDCARE,
  },
];

const outOfHomeOptions = [
  {
    label: SeekerOverhaulCCM1Labels.DAY_CARE_CENTERS,
    value: DayCareKind.DAY_CARE_CENTERS,
    description: SeekerOverhaulCCM1V2Descriptions.DAY_CARE_CENTERS,
  },
];

const Selector = ({
  careKind,
  header,
  options,
  onChangeHandler,
}: {
  careKind: CareKind;
  header: string;
  options: { label: string; value: string; description: string }[];
  onChangeHandler: (value: string[]) => void;
}) => {
  const { isDesktop } = useResponsive();

  return (
    <>
      <Typography mt={4} variant={isDesktop ? 'h2' : 'h3'}>
        {header}
      </Typography>
      <span data-hj-ignore-attributes>
        <StatelessSelector single name="careKind" onChange={onChangeHandler} sx={classes.selector}>
          {options.map((option) => (
            <Pill
              key={option.value}
              size="lg"
              label={option.label}
              value={option.value}
              selected={careKind === option.value}
              description={option.description}
              unitId={option.value}
            />
          ))}
        </StatelessSelector>
      </span>
    </>
  );
};

const CareKindRebrand = ({
  formik,
  onChangeHandler,
}: {
  formik: any;
  onChangeHandler: (value: string[]) => void;
}) => {
  const { careKind } = useSeekerCCState();
  const { isDesktop } = useResponsive();

  return (
    <FormikProvider value={formik}>
      <Head>
        <title>What can we help you find?</title>
      </Head>
      <Grid container sx={classes.mainContainer}>
        <Grid item xs={12}>
          <Header variant={isDesktop ? 'h1' : 'h2'}>What can we help you find?</Header>
        </Grid>
        <Grid item xs={12}>
          <Selector
            careKind={careKind}
            header="Care that comes to you"
            options={inHomeOptions}
            onChangeHandler={onChangeHandler}
          />
          <Selector
            careKind={careKind}
            header="Care outside your home"
            options={outOfHomeOptions}
            onChangeHandler={onChangeHandler}
          />
        </Grid>

        {!isDesktop ? (
          <StickyCTAButton ctaText="Next" onClick={formik.submitForm} />
        ) : (
          <Grid item xs={12} py={1} mt={4}>
            <Button
              onClick={formik.submitForm}
              variant="contained"
              color="primary"
              fullWidth
              size="large">
              Next
            </Button>
          </Grid>
        )}
      </Grid>
    </FormikProvider>
  );
};

export default CareKindRebrand;
